/**
 * GDSModalEventHandler.js
 * * Handles all modal-related UI events using a universal event delegation pattern.
 * It listens on the document to capture events from modals inside the main
 * content area as well as modals appended directly to the document body,
 * such as the GDSPreviewModal.
 */
export class GDSModalEventHandler {
    constructor(builder) {
        this.builder = builder;
        this.modalManager = builder.modalManager;
        this.handleModalClicks = this.handleModalClicks.bind(this);
        this.handleModalChanges = this.handleModalChanges.bind(this);
        this.handleEscKey = this.handleEscKey.bind(this);
    }

    /**
     * Attaches universal listeners to the document. This is a robust approach
     * that handles events on any modal, including ones added dynamically.
     */
    setupListeners() {
        document.addEventListener('click', this.handleModalClicks);
        document.addEventListener('change', this.handleModalChanges);
        document.addEventListener('keydown', this.handleEscKey);
    }

    /**
     * Removes the universal listeners from the document.
     */
    removeListeners() {
        document.removeEventListener('click', this.handleModalClicks);
        document.removeEventListener('change', this.handleModalChanges);
        document.removeEventListener('keydown', this.handleEscKey);
    }

    /**
     * Centralized click handler for all modal-related actions.
     * It uses event delegation to dispatch actions based on the clicked element.
     */
    handleModalClicks(e) {
        const target = e.target;

        // --- Logic for Closing Any Modal ---
        const modalClose = target.closest('.modal-close, [data-backdrop="true"]');
        if (modalClose) {
            this.handleModalClose(e, modalClose);
            return;
        }

        // --- Logic for Saving ---
        const modalSave = target.closest('.modal-save-btn');
        if (modalSave) {
            this.handleModalSave(e, modalSave);
            return;
        }

        // --- Logic for Opening Modals ---
        const previewBtn = target.closest('.preview-eye-btn');
        if (previewBtn) {
            this.handlePreviewEyeClick(e, previewBtn);
            return;
        }

        const editButton = target.closest('.edit-options-btn');
        if (editButton) {
            this.handleEditButtonClick(e, editButton);
            return;
        }

        const infoIcon = target.closest('.info-icon-container');
        if (infoIcon) {
            this.handleInfoIconClick(e, infoIcon);
            return;
        }

        const optionElement = target.closest('.option-container [data-value]');
        if (optionElement && !target.closest('input[type="checkbox"]')) {
            return this.handleModalOptionClick(e, optionElement);
        }
    }

    /**
     * Centralized change handler for form elements within modals.
     */
    handleModalChanges(e) {
        // Define the target from the event object to resolve the ReferenceError.
        const target = e.target;

        if (target.matches('.modal-option-select, .option-container input[type="checkbox"]')) {
            this.handleModalOptionChange(e, target);
        }
    }

    /**
     * A smart close handler that differentiates between modal types.
     */
    handleModalClose(e, target) {
        e.preventDefault();
        e.stopPropagation();

        const previewModal = target.closest('.gds-preview-modal');
        const cardModal = target.closest('.product-modal, .modal-container');

        if (previewModal) {
            // It's a preview modal, use its specific close method.
            // Assumes modalManager has a reference to the previewModal instance.
            this.modalManager.previewModal?.close(previewModal);
        } else if (cardModal) {
            // It's a different type of modal.
            this.modalManager.closeCardModal(cardModal);
        }
    }

    /**
     * Handles saving changes from a standard product modal.
     */
    handleModalSave(e, saveBtn) {
        e.preventDefault();
        e.stopPropagation();
        const modal = saveBtn.closest('.product-modal');
        const card = modal?.id ? this.builder.contentContainer?.querySelector(`.product-card[data-modal-id="${modal.id}"]`) : null;
        if (modal && card) this.modalManager.handleModalSave(modal, card);
    }

    /**
     * Handles the click on a preview button to open the preview modal.
     */
    handlePreviewEyeClick(e, previewBtn) {
        e.preventDefault();
        e.stopPropagation();
        const productHandle = previewBtn.dataset.productHandle;
        if (productHandle) this.modalManager.openPreviewModal(productHandle);
    }

    /**
     * Handles the click on an environment info icon to open an environment modal.
     */
    handleInfoIconClick(e, infoIcon) {
        // Prevent the event from bubbling up to the parent card
        e.preventDefault();
        e.stopPropagation();

        const modalId = infoIcon.dataset.modalTarget;
        if (modalId) {
            // Delegate to the modal manager to open the specific environment modal
            this.modalManager.openEnvironmentModal(modalId);
        }
    }

    /**
     * Handles the click on an edit button to open a standard modal.
     */
    handleEditButtonClick(e, editButton) {
        e.preventDefault();
        e.stopPropagation();
        const modalId = editButton.dataset.modalTarget;
        const modal = document.getElementById(modalId);
        if (modal) this.modalManager.openCardModal(modal);
    }

    /**
     * Handles a change event on an option selector within a modal.
     * This has been corrected to handle dropdowns and checkboxes properly.
     */
    handleModalOptionChange(e, target) {
        const field = target.dataset.field;
        const modal = target.closest('.product-modal');
        const card = modal?.id ? this.builder.contentContainer?.querySelector(`.product-card[data-modal-id="${modal.id}"]`) : null;

        if (!modal || !card || !field) {
            console.error("Could not handle option change due to missing modal, card, or field data.");
            return;
        }

        let value;

        if (target.matches('input[type="checkbox"]')) {
            // --- Checkbox Logic ---
            const isYesNa = target.dataset.isYesNa === 'true';
            if (target.checked) {
                // If checked, use its data-value (e.g., "Yes")
                value = target.dataset.value;
            } else if (isYesNa) {
                // If a Yes/NA checkbox is unchecked, use its alternate value (e.g., "N/A")
                value = target.dataset.alternateValue;
            } else {
                // If a standard checkbox is unchecked, the selection is cleared
                value = undefined;
            }
        } else {
            // --- Dropdown Logic ---
            value = target.value;
        }

        // Ensure we have the modal's temporary state to pass along
        if (!modal._tempSelections) {
            console.error('handleModalOptionChange: Modal temporary selections (_tempSelections) not found.');
            return;
        }

        // *** FIX: Call the correct method on the editModal instance ***
        if (this.modalManager?.editModal?.handleOptionSelection) {
            this.modalManager.editModal.handleOptionSelection(field, value, modal._tempSelections, modal, card);
        } else {
            console.error('handleModalOptionChange: The required method `handleOptionSelection` is not available.');
        }
    }

    /**
     * Handles the 'Escape' key press to close the currently visible modal.
     */
    handleEscKey(e) {
        if (e.key === 'Escape') {
            const visiblePreviewModal = document.querySelector('.gds-preview-modal:not(.hidden)');
            const visibleCardModal = document.querySelector('.product-modal:not(.hidden), .modal-container:not(.hidden)');

            if (visiblePreviewModal) {
                this.modalManager.previewModal?.close(visiblePreviewModal);
            } else if (visibleCardModal) {
                this.modalManager.closeCardModal(visibleCardModal);
            }
        }
    }

    /**
     * Handles a click on a custom option element (e.g., button, icon) within a modal.
     * This function extracts selection data from the DOM and delegates the update
     * logic to the modal manager.
     *
     * @param {Event} e - The click event object.
     * @param {HTMLElement} optionElement - The DOM element that was clicked.
     */
    handleModalOptionClick(e, optionElement) {
        e.preventDefault();
        e.stopPropagation();

        // Find the parent container to get the field name
        const fieldContainer = optionElement.closest('.option-container[data-field]');
        const modal = optionElement.closest('.product-modal');

        if (!modal || !fieldContainer) {
            console.error('handleModalOptionClick: Could not find parent modal or field container.');
            return;
        }

        // Find the card associated with this modal
        const card = this.builder.contentContainer?.querySelector(`.product-card[data-modal-id="${modal.id}"]`);
        if (!card) {
            console.error('handleModalOptionClick: Could not find associated card for modal:', modal.id);
            return;
        }

        // Extract the field and value from the element's data attributes
        const field = fieldContainer.dataset.field;
        const value = optionElement.dataset.value;

        // Ensure we have the necessary data
        if (field === undefined || value === undefined) {
            console.error('handleModalOptionClick: Missing field or value from option.', { field, value });
            return;
        }

        console.log(`[1] Event Caught: Clicked field='${field}', value='${value}'`);

        // The modal should have _tempSelections stored when it was opened
        if (!modal._tempSelections) {
            console.error('handleModalOptionClick: Modal temporary selections (_tempSelections) not found.');
            return;
        }

        // FIXED: Instead of trying to access through modalManager.editModal,
        // we need to get the GDSEditModal instance from the modalManager
        const editModal = this.modalManager.editModal || this.modalManager.modals?.edit;

        if (editModal && typeof editModal.handleOptionSelection === 'function') {
            // Pass the current selections directly from the modal
            editModal.handleOptionSelection(field, value, modal._tempSelections, modal, card);
        } else {
            // If editModal is not available, try to handle it directly through the builder's modal system
            console.warn('handleModalOptionClick: editModal not found, attempting direct handling');

            // Alternative approach: Call handleOptionSelection directly if we can find it
            if (this.builder.modalManager && this.builder.modalManager.editModal) {
                this.builder.modalManager.editModal.handleOptionSelection(field, value, modal._tempSelections, modal, card);
            } else {
                console.error('handleModalOptionClick: Unable to find any way to handle option selection');
            }
        }
    }
}
